// src/services/calendarConfigSchema.js
//
// Pure shape/validation layer for a site's calendar config. NO Firebase imports —
// this is the half that can be unit tested (calendarConfigSchema.test.js) and
// that the functions-side mirror is checked against.
//
// calendarConfigService.js is the I/O half; it imports everything here.

// The original hardcoded Dubois room set, now just the fallback.
// `colorId` is the Google Calendar event color (1..11) written onto the event;
// `fill`/`accent` are what RegX's own calendar grid renders (RegX draws its own
// event chips, it doesn't embed Google's UI, so these don't have to match).
export const DEFAULT_CALENDAR_ROOMS = [
  { name: "Room 1", colorId: "1", fill: "#B7A6F7", accent: "#6D28D9", label: "Violet" },
  { name: "Room 2", colorId: "2", fill: "#7FB4F5", accent: "#0369A1", label: "Sky" },
  { name: "Room 3", colorId: "5", fill: "#6FDCAE", accent: "#047857", label: "Emerald" },
  { name: "Room 4", colorId: "6", fill: "#FDBA74", accent: "#C2410C", label: "Orange" },
  { name: "Other", colorId: "8", fill: "#F294B4", accent: "#BE123C", label: "Rose" },
];

// Colors an admin can assign to a room. Google only has 11 event colors, so this
// is the full useful set; fill/accent are RegX's own chip colors for that hue.
export const CALENDAR_ROOM_PALETTE = [
  { colorId: "1", fill: "#B7A6F7", accent: "#6D28D9", label: "Violet" },
  { colorId: "2", fill: "#7FB4F5", accent: "#0369A1", label: "Sky" },
  { colorId: "5", fill: "#6FDCAE", accent: "#047857", label: "Emerald" },
  { colorId: "6", fill: "#FDBA74", accent: "#C2410C", label: "Orange" },
  { colorId: "8", fill: "#F294B4", accent: "#BE123C", label: "Rose" },
  { colorId: "3", fill: "#C4A5E8", accent: "#7E22CE", label: "Grape" },
  { colorId: "7", fill: "#8ED8E8", accent: "#0E7490", label: "Peacock" },
  { colorId: "10", fill: "#93D6A0", accent: "#15803D", label: "Basil" },
  { colorId: "11", fill: "#F3A0A0", accent: "#B91C1C", label: "Tomato" },
  { colorId: "9", fill: "#9DB0EE", accent: "#3730A3", label: "Blueberry" },
];

export const paletteEntry = (colorId) =>
  CALENDAR_ROOM_PALETTE.find((p) => p.colorId === String(colorId)) ||
  CALENDAR_ROOM_PALETTE[0];

// A site can rename rooms, but "Other" is structural: the booking modals treat
// it as "type a free-text room name", and the Apps Script keys off it too.
// Always exactly one, always last.
export const OTHER_ROOM = "Other";

export const DEFAULT_CALENDAR_CONFIG = {
  provider: "apps-script",
  label: "Research Calendar",
  timeZone: "America/New_York",
  rooms: DEFAULT_CALENDAR_ROOMS,
  // Whether the patient's name appears in the Google Calendar event TITLE and
  // description.
  //
  // Defaults to TRUE deliberately: that is what every existing event already
  // looks like, and flipping the default would silently change how a live
  // calendar reads. New sites should turn it off — the admin panel says so —
  // but no existing site's behavior changes because a field was added.
  includePatientName: true,
};

/**
 * Normalize a stored rooms array into exactly the shape the UI and the Apps
 * Script expect: named entries first, exactly one "Other", always last.
 * Falls back to the defaults rather than ever returning an empty list, because
 * an empty room list would leave coordinators unable to book at all.
 */
export const normalizeRooms = (rooms) => {
  if (!Array.isArray(rooms) || rooms.length === 0) return DEFAULT_CALENDAR_ROOMS;

  const cleaned = rooms
    .filter((r) => r && typeof r.name === "string" && r.name.trim())
    .map((r, i) => {
      const fallback = DEFAULT_CALENDAR_ROOMS[i % DEFAULT_CALENDAR_ROOMS.length];
      return {
        name: r.name.trim(),
        colorId: String(r.colorId || fallback.colorId),
        fill: r.fill || fallback.fill,
        accent: r.accent || fallback.accent,
        label: r.label || r.name.trim(),
      };
    })
    .filter((r) => r.name !== OTHER_ROOM);

  if (cleaned.length === 0) return DEFAULT_CALENDAR_ROOMS;

  const other =
    rooms.find((r) => r && r.name === OTHER_ROOM) ||
    DEFAULT_CALENDAR_ROOMS[DEFAULT_CALENDAR_ROOMS.length - 1];

  return [
    ...cleaned,
    {
      name: OTHER_ROOM,
      colorId: String(other.colorId || "8"),
      fill: other.fill || "#F294B4",
      accent: other.accent || "#BE123C",
      label: other.label || OTHER_ROOM,
    },
  ];
};

/**
 * Turn a raw Firestore document body into a fully-populated config.
 * `exists` tells the admin UI whether it's editing a real doc or starting
 * defaults it hasn't saved yet.
 */
export const shapeCalendarConfig = (data, exists) => {
  if (!exists) return { ...DEFAULT_CALENDAR_CONFIG, exists: false };
  const d = data || {};
  return {
    exists: true,
    provider: d.provider || DEFAULT_CALENDAR_CONFIG.provider,
    label: d.label || DEFAULT_CALENDAR_CONFIG.label,
    timeZone: d.timeZone || DEFAULT_CALENDAR_CONFIG.timeZone,
    // calendarId is shown in the admin UI so someone can confirm which calendar
    // a site points at. Not a secret — anyone with access to the calendar
    // already sees it — but the endpoint URL and shared secret are NOT here.
    calendarId: d.calendarId || "",
    rooms: normalizeRooms(d.rooms),
    includePatientName:
      d.includePatientName === undefined ? true : !!d.includePatientName,
  };
};

/** Strip a config down to exactly the fields that get persisted. */
export const toCalendarConfigPayload = (config, now) => ({
  provider: config.provider || DEFAULT_CALENDAR_CONFIG.provider,
  label: config.label || DEFAULT_CALENDAR_CONFIG.label,
  timeZone: config.timeZone || DEFAULT_CALENDAR_CONFIG.timeZone,
  calendarId: (config.calendarId || "").trim(),
  rooms: normalizeRooms(config.rooms),
  includePatientName:
    config.includePatientName === undefined ? true : !!config.includePatientName,
  updatedAt: now || new Date().toISOString(),
});

/**
 * Validate what the admin panel is about to save. Returns an error string, or
 * "" when it's fine.
 */
export const validateCalendarConfig = (config) => {
  const named = (config.rooms || []).filter(
    (r) => r && r.name && r.name.trim() && r.name !== OTHER_ROOM
  );
  if (!named.length) return "Add at least one room.";

  const names = named.map((r) => r.name.trim().toLowerCase());
  if (new Set(names).size !== names.length) return "Two rooms have the same name.";

  if (config.provider !== "none" && !(config.calendarId || "").trim()) {
    return "A calendar ID is required for a connected calendar.";
  }
  return "";
};
